import React, { FC, memo } from 'react'

import IcBell from '@interco/icons/core/action-navigation/ic_bell'
import IcBellNotification from '@interco/icons/core/action-navigation/ic_bell_notification'
import IcCheck from '@interco/icons/core/action-navigation/ic_check'
import IcChecklist from '@interco/icons/core/action-navigation/ic_checklist'
import IcClock from '@interco/icons/core/action-navigation/ic_clock'
import IcCopy from '@interco/icons/core/action-navigation/ic_copy'
import IcEllipsis from '@interco/icons/core/action-navigation/ic_ellipsis'
import IcSearchError from '@interco/icons/core/action-navigation/ic_search_error'
import IcSliders from '@interco/icons/core/action-navigation/ic_sliders'
import IcSwapDots from '@interco/icons/core/action-navigation/ic_swap_dots'
import IcSwapVertical from '@interco/icons/core/action-navigation/ic_swap_vertical'
import IcInterLoop from '@interco/icons/core/brands/ic_inter_loop'
import IcWhatsapp from '@interco/icons/core/brands/ic_whatsapp'
import IcDocument from '@interco/icons/core/files/ic_document'
import IcCreditCard from '@interco/icons/core/finance/ic_credit_card'
import IcCurrencyExchange from '@interco/icons/core/finance/ic_currency_exchange'
import IcTrendingUp from '@interco/icons/core/finance/ic_trending_up'
import IcForkKnife from '@interco/icons/core/home-objects/ic_fork_knife'
import IcGlobe from '@interco/icons/core/home-objects/ic_globe'
import IcUmbrella from '@interco/icons/core/home-objects/ic_umbrella'
import IcHandMoney from '@interco/icons/core/human/ic_hand_money'
import IcUserMoney from '@interco/icons/core/human/ic_user_money'
import IcFlagBrazil from '@interco/icons/core/misc/ic_flag_brazil'
import IcFlagUnitedStates from '@interco/icons/core/misc/ic_flag_united_states'
import IcEnvelope from '@interco/icons/core/media-communication/ic_envelope'
import IcMessageEllipsis from '@interco/icons/core/media-communication/ic_message_ellipsis'
import IcPhone from '@interco/icons/core/media-communication/ic_phone'
import IcSmartphone from '@interco/icons/core/media-communication/ic_smartphone'
import IcSmartphoneMoney from '@interco/icons/core/media-communication/ic_smartphone_money'
import IcKey from '@interco/icons/core/privacy-security/ic_key'
import IcPadlockOpen from '@interco/icons/core/privacy-security/ic_padlock_open'
import IcGift from '@interco/icons/core/shopping/ic_gift'
import IcShoppingCart from '@interco/icons/core/shopping/ic_shopping_cart'
import IcTicketVertical from '@interco/icons/core/shopping/ic_ticket_vertical'
import IcCheckCircle from '@interco/icons/core/status/ic_check_circle'
import IcCloseCircle from '@interco/icons/core/status/ic_close_circle'
import IcCloseCircleFill from '@interco/icons/core/status/ic_close_circle_fill'
import IcHelpCircle from '@interco/icons/core/status/ic_help_circle'
import IcInfoCircle from '@interco/icons/core/status/ic_info_circle'
import IcInfoCircleFill from '@interco/icons/core/status/ic_info_circle_fill'
import IcBank from '@interco/icons/core/travel-mobility/ic_bank'
import IcPlaneDeparture from '@interco/icons/core/travel-mobility/ic_plane_departure'
import IcPdf from '@interco/icons/core/files/ic_file_pdf'

import { TInterIcon } from '../types/InterSvgIcon.types'
import { useInterSvgIcon } from '../hooks/useInterSvgIcon'
import { InterSvgIconView } from './InterSvgIconView'

type TIconProps = Pick<TInterIcon, 'iconRef' | 'width' | 'height' | 'color' | 'onClick'>

const Icon: FC<TInterIcon> = memo(({ iconRef, width, height, color, skeletonShape, onClick }) => {
  const { borderRadius, importedIcon } = useInterSvgIcon({
    iconRef,
    skeletonShape,
    width,
    height,
    color,
    onClick,
  })

  return (
    <InterSvgIconView borderRadius={borderRadius} importedIcon={importedIcon} onClick={onClick} />
  )
})

export const InterSvgIcon: FC<TIconProps> = ({ iconRef, width, height, color, onClick }) => {
  const iconProps = { width, height, color, onClick }

  switch (iconRef) {
    case 'core/action-navigation/ic_bell':
      return <IcBell {...iconProps} />
    case 'core/action-navigation/ic_bell_notification':
      return <IcBellNotification {...iconProps} />
    case 'core/action-navigation/ic_check':
      return <IcCheck {...iconProps} />
    case 'core/action-navigation/ic_checklist':
      return <IcChecklist {...iconProps} />
    case 'core/action-navigation/ic_clock':
      return <IcClock {...iconProps} />
    case 'core/action-navigation/ic_copy':
      return <IcCopy {...iconProps} />
    case 'core/action-navigation/ic_ellipsis':
      return <IcEllipsis {...iconProps} />
    case 'core/action-navigation/ic_search_error':
      return <IcSearchError {...iconProps} />
    case 'core/action-navigation/ic_sliders':
      return <IcSliders {...iconProps} />
    case 'core/action-navigation/ic_swap_dots':
      return <IcSwapDots {...iconProps} />
    case 'core/action-navigation/ic_swap_vertical':
      return <IcSwapVertical {...iconProps} />
    case 'core/brands/ic_inter_loop':
      return <IcInterLoop {...iconProps} />
    case 'core/brands/ic_whatsapp':
      return <IcWhatsapp {...iconProps} />
    case 'core/files/ic_document':
      return <IcDocument {...iconProps} />
    case 'core/finance/ic_credit_card':
      return <IcCreditCard {...iconProps} />
    case 'core/finance/ic_currency_exchange':
      return <IcCurrencyExchange {...iconProps} />
    case 'core/finance/ic_trending_up':
      return <IcTrendingUp {...iconProps} />
    case 'core/home-objects/ic_fork_knife':
      return <IcForkKnife {...iconProps} />
    case 'core/home-objects/ic_globe':
      return <IcGlobe {...iconProps} />
    case 'core/home-objects/ic_umbrella':
      return <IcUmbrella {...iconProps} />
    case 'core/human/ic_hand_money':
      return <IcHandMoney {...iconProps} />
    case 'core/human/ic_user_money':
      return <IcUserMoney {...iconProps} />
    case 'core/misc/ic_flag_brazil':
      return <IcFlagBrazil {...iconProps} />
    case 'core/misc/ic_flag_united_states':
      return <IcFlagUnitedStates {...iconProps} />
    case 'core/media-communication/ic_envelope':
      return <IcEnvelope {...iconProps} />
    case 'core/media-communication/ic_message_ellipsis':
      return <IcMessageEllipsis {...iconProps} />
    case 'core/media-communication/ic_phone':
      return <IcPhone {...iconProps} />
    case 'core/media-communication/ic_smartphone':
      return <IcSmartphone {...iconProps} />
    case 'core/media-communication/ic_smartphone_money':
      return <IcSmartphoneMoney {...iconProps} />
    case 'core/privacy-security/ic_key':
      return <IcKey {...iconProps} />
    case 'core/privacy-security/ic_padlock_open':
      return <IcPadlockOpen {...iconProps} />
    case 'core/shopping/ic_gift':
      return <IcGift {...iconProps} />
    case 'core/shopping/ic_shopping_cart':
      return <IcShoppingCart {...iconProps} />
    case 'core/shopping/ic_ticket_vertical':
      return <IcTicketVertical {...iconProps} />
    case 'core/status/ic_check_circle':
      return <IcCheckCircle {...iconProps} />
    case 'core/status/ic_close_circle':
      return <IcCloseCircle {...iconProps} />
    case 'core/status/ic_close_circle_fill':
      return <IcCloseCircleFill {...iconProps} />
    case 'core/status/ic_help_circle':
      return <IcHelpCircle {...iconProps} />
    case 'core/status/ic_info_circle':
      return <IcInfoCircle {...iconProps} />
    case 'core/status/ic_info_circle_fill':
      return <IcInfoCircleFill {...iconProps} />
    case 'core/travel-mobility/ic_bank':
      return <IcBank {...iconProps} />
    case 'core/travel-mobility/ic_plane_departure':
      return <IcPlaneDeparture {...iconProps} />
    case 'core/files/ic_file_pdf':
      return <IcPdf {...iconProps} />
    default:
      return <Icon iconRef={iconRef} {...iconProps} />
  }
}
